import React, { useState, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { ExceptionOrderFilters, ExceptionSubTab } from '../../../../graphql/types/ExceptionOrderTypes';
import { allChannelResponse, GET_ALL_CHANNEL } from '../../../../graphql/queries/getChannelAll';
import { ExceptionDictionaryResponse, ExceptionDictionaryType, GET_ALL_EXCEPTION } from '../../../../pages/wms/exceptions/graphql/queries/getAllException';
import { GET_WAREHOUSE_LOCATION_PAGE_LIST, GetWarehouseLocationPageListResponse, WarehouseLocationInfoRequestInput } from '../../../../pages/wms/location-management/graphql/queries/getWarehouseLocationPageList';
import { useStatusInternationalization } from '../../../../pages/delivery/parcels/hooks/useStatusInternationalization';
import { GET_INT_ENUM_VALUES, GetIntEnumValuesResponse } from '../../../../graphql/queries/getEnumValues';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import BatchInputFilterItem from '../../../delivery/components/BatchInputFilterItem';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';
import DateRangeFilterItem from '../../../delivery/components/DateRangeFilterItem';

interface ExceptionOrderFilterProps {
    filters: ExceptionOrderFilters;
    onFilterChange: (filters: ExceptionOrderFilters) => void;
    onSearch: () => void;
    onReset: () => void;
    currentTab: string;
    currentSubTab?: ExceptionSubTab;
}

/**
 * 根据tab和subTab获取预设的异常原因码
 */
function getPresetExceptionReasonCodes(tab: string, subTab?: ExceptionSubTab): string[] {
    switch (tab) {
        case 'WAREHOUSE_SUSPECTED_LOST':
            return ['ERWHS006'];
        case 'DRIVER_RETURN':
            // 与后端「上架允许异常码白名单」（PutawayAllowedExceptionCodes）默认值保持一致；
            // ERDL006 / ERDSP008 为「地址无法投递」成对码，缺失会导致用户在司机退回筛选里选不到这两类。
            return ['ERDSP004', 'ERDSP005', 'ERDSP006', 'ERDSP007', 'ERDSP008', 'ERDL001', 'ERDL002', 'ERDL003', 'ERDL004', 'ERDL006', 'ERWHS008', 'ERWHS009'];
        case 'OTHER_EXCEPTIONS':
            if (subTab === 'missing') {
                return ['ERHUB003', 'ERHUB005', 'ERHUB008', 'ERWHS003', 'ERWHS004', 'ERDSP003'];
            } else if (subTab === 'long_missing') {
                return ['ERHUB009', 'ERWHS010'];
            } else if (subTab === 'damaged') {
                return ['ERHUB001', 'ERHUB006', 'ERHUB007', 'ERWHS001', 'ERWHS005', 'ERDSP001'];
            } else if (subTab === 'missort') {
                return ['ERHUB002', 'ERWHS002', 'ERDSP002'];
            } else if (subTab === 'oversize') {
                return ['ERHUB004', 'ERWHS007'];
            } else {
                // 主tab：包含所有子tab的异常码
                return [
                    'ERHUB003', 'ERHUB005', 'ERHUB008', 'ERWHS003', 'ERWHS004', 'ERDSP003',
                    'ERHUB009', 'ERWHS010',
                    'ERHUB001', 'ERHUB006', 'ERHUB007', 'ERWHS001', 'ERWHS005', 'ERDSP001',
                    'ERHUB002', 'ERWHS002', 'ERDSP002',
                    'ERHUB004', 'ERWHS007'
                ];
            }
        default:
            return [];
    }
}

/**
 * 根据tab和subTab获取预设的包裹状态
 * 
 * 后端通过overallStatus来控制包裹状态，不同的tab/subTab有不同的预设
 * 对应的OverallStatus值：
 * - ARRIVED_WHS = 38
 * - HOLDING = 82
 */
function getPresetParcelStatus(tab: string, subTab?: ExceptionSubTab): number[] | null {
    switch (tab) {
        case 'DRIVER_RETURN':
            // 司机退回：根据子Tab有不同的预设
            switch (subTab) {
                case 'pending_putaway':
                    // 待上架：overallStatus=ARRIVED_WHS (值: 38)
                    return [38];
                case 'putaway':
                case 'pending_picking':
                    // 已上架、待下架：overallStatus=HOLDING (值: 82)
                    return [82];
                case 'picked':
                    // 已下架：无限制
                    return null;
                case 'all':
                default:
                    // 全部退回：只能是HOLDING或ARRIVED_WHS (值: 82, 38)
                    return [82, 38];
            }
        default:
            // 其他tab：无预设限制
            return null;
    }
}

/**
 * 根据tab和subTab获取预设的下一步动作
 * 
 * 后端对DRIVER_RETURN的各个子tab有不同的nextAction预设
 * ExceptionAction值：
 * - Hold = 1
 * - ReDeliver = 2
 * - Return = 3
 * - Compensate = 4
 * - ReSort = 5
 */
function getPresetNextAction(tab: string, subTab?: ExceptionSubTab): number[] | null {
    switch (tab) {
        case 'DRIVER_RETURN':
            // 司机退回：根据子Tab有不同的预设
            switch (subTab) {
                case 'pending_putaway':
                case 'putaway':
                    // 待上架、已上架：nextAction=HOLD (值: 1)
                    return [1];
                case 'pending_picking':
                    // 待下架：nextAction!=HOLD，即除了1以外的其他动作 (2, 3, 4, 5)
                    return [2, 3, 4, 5];
                case 'picked':
                case 'all':
                default:
                    // 已下架、全部退回：无限制
                    return null;
            }
        default:
            // 其他tab：无预设限制
            return null;
    }
}

interface CustomerOption {
    value: string;
    label: string;
}

const ExceptionOrderFilter: React.FC<ExceptionOrderFilterProps> = ({
    filters,
    onFilterChange,
    onSearch,
    onReset,
    currentTab,
    currentSubTab
}) => {
    const { t } = useTranslation();
    const { getExceptionReasonText } = useStatusInternationalization();
    const { currentWarehouse } = useWarehouseContext();

    // 查询客户列表
    const { data: channelData } = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const [channelOptions, setChannelOptions] = useState<CustomerOption[]>([]);

    // 查询异常原因列表
    const { data: exceptionData } = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const [exceptionSelectData, setExceptionSelectData] = useState<ExceptionDictionaryType[]>([]);

    // 查询枚举值列表
    const { data: overallStatusEnumData } = useQuery<GetIntEnumValuesResponse>(GET_INT_ENUM_VALUES, {
        variables: { enumType: 'OverallStatus' }
    });
    const { data: exceptionStatusEnumData } = useQuery<GetIntEnumValuesResponse>(GET_INT_ENUM_VALUES, {
        variables: { enumType: 'ExceptionOrderStatus' }
    });

    // 查询库位列表（用于下拉选择）
    const warehouseLocationRequest: WarehouseLocationInfoRequestInput = useMemo(() => ({
        pageRequest: { pageNum: 1, pageSize: 1000 },
        filters: currentWarehouse?.id ? { warehouseId: Number(currentWarehouse.id) } : undefined
    }), [currentWarehouse?.id]);

    const { data: locationData } = useQuery<GetWarehouseLocationPageListResponse>(
        GET_WAREHOUSE_LOCATION_PAGE_LIST,
        {
            variables: { request: warehouseLocationRequest },
            skip: !currentWarehouse?.id
        }
    );

    // 库位选项列表
    const locationOptions = useMemo(() => {
        if (!locationData?.getWarehouseLocationPageList?.warehouseLocationInfoList) {
            return [];
        }
        return locationData.getWarehouseLocationPageList.warehouseLocationInfoList.map(loc => ({
            value: loc.locationNo,
            label: loc.locationNo
        }));
    }, [locationData]);

    // 是否显示司机退回专用筛选（库位号和存储编号）
    const showDriverReturnFilters = currentTab === 'DRIVER_RETURN' &&
        (currentSubTab === 'all' || 
         currentSubTab === 'putaway' || 
         currentSubTab === 'pending_picking' || 
         currentSubTab === 'picked');

    // 是否显示上架时间筛选
    const showPutawayTimeFilter = currentTab === 'DRIVER_RETURN' &&
        (currentSubTab === 'putaway' || 
         currentSubTab === 'pending_picking' || 
         currentSubTab === 'picked');

    // 处理客户选项
    useEffect(() => {
        if (channelData?.getAllChannels) {
            const channels = Array.isArray(channelData.getAllChannels) 
                ? channelData.getAllChannels 
                : [channelData.getAllChannels];
            const options = channels.map(channel => ({
                value: channel.id?.toString() || '',
                label: channel.channelName
            }));
            setChannelOptions(options);
        }
    }, [channelData]);

    // 处理异常原因选项
    useEffect(() => {
        if (exceptionData?.getExceptionDictionaryAll) {
            setExceptionSelectData(exceptionData.getExceptionDictionaryAll);
        }
    }, [exceptionData]);

    // 构建异常时间值（用于DatePicker）
    const markExceptionAtValue: [string | null, string | null] | null = 
        filters.markExceptionAtFrom || filters.markExceptionAtTo
            ? [
                filters.markExceptionAtFrom ? dayjs.unix(filters.markExceptionAtFrom).format('YYYY-MM-DD') : null,
                filters.markExceptionAtTo ? dayjs.unix(filters.markExceptionAtTo).format('YYYY-MM-DD') : null
            ]
            : null;

    // 构建回仓时间值
    const returnToWhsAtValue: [string | null, string | null] | null = 
        filters.returnToWhsAtFrom || filters.returnToWhsAtTo
            ? [
                filters.returnToWhsAtFrom ? dayjs.unix(filters.returnToWhsAtFrom).format('YYYY-MM-DD') : null,
                filters.returnToWhsAtTo ? dayjs.unix(filters.returnToWhsAtTo).format('YYYY-MM-DD') : null
            ]
            : null;

    // 构建上架时间值
    const putawayAtValue: [string | null, string | null] | null =
        filters.putawayAtFrom || filters.putawayAtTo
            ? [
                filters.putawayAtFrom ? dayjs.unix(filters.putawayAtFrom).format('YYYY-MM-DD') : null,
                filters.putawayAtTo ? dayjs.unix(filters.putawayAtTo).format('YYYY-MM-DD') : null
            ]
            : null;

    // 最大日期范围：14天
    const MAX_DATE_RANGE_DAYS = 14;

    // 处理异常时间变化：限制14天范围，超过则自动调整
    const handleExceptionTimeChange = (dates: [string | null, string | null] | null) => {
        // 由于 allowClear={false}，dates 不会为 null
        if (!dates || !dates[0] || !dates[1]) return;

        const startDate = dayjs(dates[0]);
        const endDate = dayjs(dates[1]);
        const daysDiff = endDate.diff(startDate, 'day');

        // 如果日期范围超过14天，自动调整结束日期
        if (daysDiff > MAX_DATE_RANGE_DAYS - 1) {
            const adjustedEndDate = startDate.add(MAX_DATE_RANGE_DAYS - 1, 'day');
            message.warning(t('wms.exceptionorder.message.dateRangeExceeded', { days: MAX_DATE_RANGE_DAYS }));
            onFilterChange({
                ...filters,
                markExceptionAtFrom: startDate.startOf('day').unix(),
                markExceptionAtTo: adjustedEndDate.endOf('day').unix()
            });
        } else {
            onFilterChange({
                ...filters,
                markExceptionAtFrom: startDate.startOf('day').unix(),
                markExceptionAtTo: endDate.endOf('day').unix()
            });
        }
    };

    // 处理回仓时间变化
    const handleReturnToWhsTimeChange = (dates: [string | null, string | null] | null) => {
        onFilterChange({
            ...filters,
            returnToWhsAtFrom: dates?.[0] ? dayjs(dates[0]).startOf('day').unix() : undefined,
            returnToWhsAtTo: dates?.[1] ? dayjs(dates[1]).endOf('day').unix() : undefined
        });
    };

    // 处理上架时间变化
    const handlePutawayTimeChange = (dates: [string | null, string | null] | null) => {
        onFilterChange({
            ...filters,
            putawayAtFrom: dates?.[0] ? dayjs(dates[0]).startOf('day').unix() : undefined,
            putawayAtTo: dates?.[1] ? dayjs(dates[1]).endOf('day').unix() : undefined
        });
    };

    // 定义所有标签数组，用于统一宽度计算
    const labelTracking = t('wms.exceptions.filters.tracking');
    const allLabels = [
        labelTracking,
        t('wms.exceptions.filters.client'),
        t('wms.exceptions.filters.exceptionTime'),
        t('wms.exceptions.filters.parcelStatus'),
        t('wms.exceptions.filters.exceptionReason'),
        t('wms.exceptionorder.filters.returnToWhsTime'),
        t('wms.exceptions.filters.exceptionStatus'),
        ...(currentTab === 'DRIVER_RETURN' ? [t('wms.exceptions.filters.nextAction')] : []),
        ...(showPutawayTimeFilter ? [t('wms.exceptionorder.filters.putawayTime')] : []),
        ...(showDriverReturnFilters ? [
            t('wms.exceptionorder.filters.warehouseLocationCode'),
            t('wms.exceptionorder.filters.storageNumber')
        ] : [])
    ];

    // 异常原因选项
    const exceptionReasonOptions = useMemo(() => {
        // 获取当前tab的预设异常码
        const presetCodes = getPresetExceptionReasonCodes(currentTab, currentSubTab);
        
        // 从全量异常字典中过滤出预设异常码对应的选项
        return exceptionSelectData
            .filter(ex => presetCodes.includes(ex.exceptionReasonCode))
            .map(ex => ({
                value: ex.exceptionReasonCode,
                label: String(getExceptionReasonText(ex, ex.exceptionReasonCode))
            }));
    }, [currentTab, currentSubTab, exceptionSelectData, getExceptionReasonText]);

    // 包裹状态选项（从后端枚举获取，过滤掉ASSIGNED默认值）
    const parcelStatusOptions = useMemo(() => {
        const enumValues = overallStatusEnumData?.getIntEnumValues || [];
        const allOptions = enumValues
            .filter(item => item.name !== 'ASSIGNED')
            .map(item => ({
                value: String(item.value),
                label: t(`enum.overallStatus.${item.name}`)
            }));

        // 根据当前tab和subTab过滤包裹状态选项
        const presetParcelStatus = getPresetParcelStatus(currentTab, currentSubTab);
        if (presetParcelStatus) {
            return allOptions.filter(option => presetParcelStatus.includes(Number(option.value)));
        }

        return allOptions;
    }, [currentTab, currentSubTab, t, overallStatusEnumData]);

    // 异常状态选项（从后端枚举获取）
    const exceptionStatusOptions = useMemo(() => {
        const enumValues = exceptionStatusEnumData?.getIntEnumValues || [];
        return enumValues.map(item => ({
            value: String(item.value),
            label: t(`wms.exceptionorder.exceptionStatus.${item.name}`)
        }));
    }, [exceptionStatusEnumData, t]);

    // 下一步动作选项（根据tab/subTab过滤）
    const nextActionOptions = useMemo(() => {
        const allOptions = [
            { value: '1', label: t('config.nextAction.hold') },
            { value: '2', label: t('config.nextAction.reDeliver') },
            { value: '3', label: t('config.nextAction.return') },
            { value: '5', label: t('config.nextAction.reSort') }
        ];
        
        // 根据当前tab和subTab过滤下一步动作选项
        const presetNextAction = getPresetNextAction(currentTab, currentSubTab);
        if (presetNextAction) {
            // 如果有预设，仅显示预设的下一步动作
            return allOptions.filter(option => presetNextAction.includes(Number(option.value)));
        }
        
        return allOptions;
    }, [currentTab, currentSubTab, t]);

    return (
        <div className="mb-2 py-2">
            {/* Tracking 独占一行 */}
            <div className="mb-4">
                <BatchInputFilterItem
                    label={labelTracking}
                    value={filters.trackingNumbers}
                    onChange={(e) => onFilterChange({ ...filters, trackingNumbers: e.target.value || undefined })}
                    placeholder={t('wms.exceptions.filters.tracking') as string}
                    spanClassName={getSmartLabelClassName(allLabels)}
                    rows={2}
                />
            </div>

            {/* 使用 Grid 布局，每行3列，自动换行 */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-10 gap-y-4">
                {/* 第1行：客户、异常状态 */}
                
                {/* 客户 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('wms.exceptions.filters.client')}
                        options={channelOptions}
                        value={filters.clientId}
                        onChange={(value: string) => onFilterChange({ ...filters, clientId: value || undefined })}
                        spanClassName={getSmartLabelClassName(allLabels)}
                        placeholder={t('wms.exceptions.filters.all') as string}
                    />
                </div>

                {/* 异常状态 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('wms.exceptions.filters.exceptionStatus')}
                        options={exceptionStatusOptions}
                        value={filters.exceptionStatus?.map(String) ?? []}
                        onChange={(values: string[]) => {
                            // 防止清空：如果尝试清空，显示提示并保持当前值
                            if (!values || values.length === 0) {
                                message.warning(t('wms.exceptionorder.message.exceptionStatusCannotBeEmpty'));
                                return;
                            }
                            onFilterChange({ ...filters, exceptionStatus: values.map(v => Number(v)) });
                        }}
                        spanClassName={getSmartLabelClassName(allLabels)}
                        mode="multiple"
                        placeholder={t('components.input.all') as string}
                        allowClear={false}
                    />
                </div>

                {/* 第2行：异常时间、回仓时间、包裹状态 */}

                {/* 异常时间 */}
                <div className="min-w-0">
                    <DateRangeFilterItem
                        label={t('wms.exceptions.filters.exceptionTime')}
                        value={markExceptionAtValue}
                        onChange={handleExceptionTimeChange}
                        spanClassName={getSmartLabelClassName(allLabels)}
                        allowClear={false}
                    />
                </div>

                {/* 回仓时间 */}
                <div className="min-w-0">
                    <DateRangeFilterItem
                        label={t('wms.exceptionorder.filters.returnToWhsTime')}
                        value={returnToWhsAtValue}
                        onChange={handleReturnToWhsTimeChange}
                        spanClassName={getSmartLabelClassName(allLabels)}
                    />
                </div>

                {/* 包裹状态 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('wms.exceptions.filters.parcelStatus')}
                        options={parcelStatusOptions}
                        value={filters.overallStatus?.map(String) ?? []}
                        onChange={(values: string[]) => onFilterChange({ ...filters, overallStatus: values && values.length > 0 ? values.map(v => Number(v)) : undefined })}
                        spanClassName={getSmartLabelClassName(allLabels)}
                        mode="multiple"
                        maxTagCount="responsive"
                        placeholder={t('components.input.all') as string}
                    />
                </div>

                {/* 第3行：异常原因、下一步动作（条件显示）、库位号（条件显示）、存储编号（条件显示） */}

                {/* 异常原因 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('wms.exceptions.filters.exceptionReason')}
                        options={exceptionReasonOptions}
                        value={filters.exceptionReasonCodes ?? []}
                        onChange={(values: string[]) => onFilterChange({ ...filters, exceptionReasonCodes: values && values.length > 0 ? values : undefined })}
                        spanClassName={getSmartLabelClassName(allLabels)}
                        mode="multiple"
                        maxTagCount="responsive"
                        placeholder={t('wms.exceptions.filters.all') as string}
                    />
                </div>

                {/* 下一步动作（条件显示：仅DRIVER_RETURN tab显示） */}
                {currentTab === 'DRIVER_RETURN' && (
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('wms.exceptions.filters.nextAction')}
                            options={nextActionOptions}
                            value={filters.nextActions?.map(String) ?? []}
                            onChange={(values: string[]) => onFilterChange({ ...filters, nextActions: values && values.length > 0 ? values.map(v => Number(v)) : undefined })}
                            spanClassName={getSmartLabelClassName(allLabels)}
                            mode="multiple"
                            placeholder={t('components.input.all') as string}
                        />
                    </div>
                )}

                {/* 库位号（条件显示） */}
                {showDriverReturnFilters && (
                    <div className="min-w-0">
                        <ParcelFilterStyle
                            label={t('wms.exceptionorder.filters.warehouseLocationCode')}
                            options={locationOptions}
                            value={filters.warehouseLocationCode}
                            onChange={(value: string) => onFilterChange({ ...filters, warehouseLocationCode: value })}
                            spanClassName={getSmartLabelClassName(allLabels)}
                            placeholder={t('components.input.select') as string}
                        />
                    </div>
                )}

                {/* 存储编号（条件显示） */}
                {showDriverReturnFilters && (
                    <div className="min-w-0">
                        <BatchInputFilterItem
                            label={t('wms.exceptionorder.filters.storageNumber')}
                            value={filters.storageNumber}
                            onChange={(e) => onFilterChange({ ...filters, storageNumber: e.target.value })}
                            spanClassName={getSmartLabelClassName(allLabels)}
                            placeholder={t('components.input.typeIn') as string}
                            rows={1}
                        />
                    </div>
                )}

                {/* 第4行：上架时间（条件显示） */}

                {/* 上架时间（条件显示） */}
                {showPutawayTimeFilter && (
                    <div className="min-w-0">
                        <DateRangeFilterItem
                            label={t('wms.exceptionorder.filters.putawayTime')}
                            value={putawayAtValue}
                            onChange={handlePutawayTimeChange}
                            spanClassName={getSmartLabelClassName(allLabels)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExceptionOrderFilter;
